import React, { PropsWithChildren, useRef } from 'react'
import { motion, MotionValue, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { cn } from '../../utils/ui'

export const Dock = React.forwardRef(({
  className,
  children,
  iconSize = 40,
  iconMagnification = 60,
  disableMagnification = false,
  iconDistance = 140,
  direction = 'middle',
  ...props
}, ref) => {
  const mouseX = useMotionValue(Infinity)

  const renderChildren = () => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === DockIcon) {
        return React.cloneElement(child, {
          ...child.props,
          mouseX: mouseX,
          size: iconSize,
          magnification: iconMagnification,
          disableMagnification: disableMagnification,
          distance: iconDistance,
        })
      }
      return child
    })
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      {...props}
      className={cn(
        'supports-backdrop-blur:bg-white/10 supports-backdrop-blur:dark:bg-black/10 mx-auto mt-8 flex h-[58px] w-max items-center justify-center gap-2 rounded-2xl border p-2 backdrop-blur-md',
        {
          'items-start': direction === 'top',
          'items-center': direction === 'middle',
          'items-end': direction === 'bottom',
        },
        className
      )}
    >
      {renderChildren()}
    </motion.div>
  )
})

Dock.displayName = 'Dock'

export const DockIcon = ({
  size = 40,
  magnification = 60,
  disableMagnification,
  distance = 140,
  mouseX,
  className,
  children,
  ...props
}) => {
  const ref = useRef(null)
  const padding = Math.max(6, size * 0.2)
  const defaultMouseX = useMotionValue(Infinity)

  const distanceCalc = useTransform(mouseX ?? defaultMouseX, (val) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 }
    return val - bounds.x - bounds.width / 2
  })

  const targetSize = disableMagnification ? size : magnification

  const sizeTransform = useTransform(
    distanceCalc,
    [-distance, 0, distance],
    [size, targetSize, size],
  )

  const scaleSize = useSpring(sizeTransform, {
    mass: 0.1,
    stiffness: 150,
    damping: 12,
  })

  return (
    <motion.div
      ref={ref}
      style={{ width: scaleSize, height: scaleSize, padding }}
      className={cn(
        'flex aspect-square cursor-pointer items-center justify-center rounded-full',
        disableMagnification && 'transition-colors hover:bg-muted-foreground',
        className,
      )}
      {...props}
    >
      <div>{children}</div>
    </motion.div>
  )
}

DockIcon.displayName = 'DockIcon'